import QRCode from 'qrcode';

/**
 * Anzeige in der Authenticator-App: obere Zeile (Issuer).
 * Doppelpunkte werden entfernt, sie trennen im otpauth-Format Issuer und Konto.
 */
function sauber(text: string) {
  return text.replace(/:/g, ' ').replace(/\s+/g, ' ').trim();
}

export const TOTP_AUSSTELLER = {
  /** Mandanten-App: "merkan | <Mandant>", bevorzugt mit Kurzname */
  mandant: (mandantName: string) => sauber(`merkan | ${mandantName || 'Mandant'}`),
  admin: 'merkan | Admin Center',
} as const;

/** Logo für Apps, die den image-Parameter auswerten (z. B. FreeOTP). Andere Apps ignorieren ihn. */
const LOGO_PFAD = '/icon-512.png';

/** Ergänzt die von Supabase gelieferte otpauth-URI um das Merkan-Logo. */
function mitLogo(uri: string) {
  try {
    const url = new URL(uri);
    url.searchParams.set('image', new URL(LOGO_PFAD, window.location.origin).toString());
    return url.toString();
  } catch {
    return uri;
  }
}

/** Liest Issuer, Konto und Schlüssel aus der otpauth-URI für die manuelle Eingabe. */
export function totpDetails(uri: string): { aussteller: string; konto: string; schluessel: string } {
  try {
    const url = new URL(uri);
    const label = decodeURIComponent(url.pathname.replace(/^\/+/, '').replace(/^totp\//, ''));
    const teile = label.split(':');
    const konto = teile.length > 1 ? teile.slice(1).join(':') : label;
    return {
      aussteller: url.searchParams.get('issuer') ?? (teile.length > 1 ? teile[0] : ''),
      konto: konto.trim(),
      schluessel: url.searchParams.get('secret') ?? '',
    };
  } catch {
    return { aussteller: '', konto: '', schluessel: '' };
  }
}

/** Schlüssel in Vierergruppen, leichter abzutippen */
export function schluesselGruppiert(s: string) {
  return s.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
}

const FARBE = '#16233A';
const AKZENT = '#2FB39A';
const PAPIER = '#F4F1EA';

/** Merkan-Zeichen (aus public/marke/merkan-mark.svg), Koordinaten 0 bis 64 */
function zeichen(x: number, y: number, groesse: number) {
  const s = groesse / 64;
  return `<g transform="translate(${x} ${y}) scale(${s})">`
    + `<rect width="64" height="64" rx="14" fill="${FARBE}"/>`
    + `<path d="M14 46V30a9 9 0 0 1 18 0v16" fill="none" stroke="${PAPIER}" stroke-width="5" stroke-linecap="round"/>`
    + `<path d="M32 46V30a9 9 0 0 1 18 0v6" fill="none" stroke="${AKZENT}" stroke-width="5" stroke-linecap="round"/>`
    + `<rect x="47" y="43" width="7" height="7" rx="1.6" fill="${AKZENT}" transform="rotate(18 50.5 46.5)"/>`
    + `</g>`;
}

/** Ecke des QR-Codes (Suchmuster) als abgerundetes Quadrat mit Kern */
function suchmuster(x: number, y: number) {
  return `<rect x="${x + 0.5}" y="${y + 0.5}" width="6" height="6" rx="1.9" fill="none" stroke="${FARBE}" stroke-width="1"/>`
    + `<rect x="${x + 2}" y="${y + 2}" width="3" height="3" rx="0.9" fill="${FARBE}"/>`;
}

/**
 * Erzeugt einen modernen QR-Code als SVG: runde Punkte, abgerundete Ecken,
 * Merkan-Zeichen in der Mitte. Fehlerkorrektur Q (25 %), das Zeichen in der Mitte
 * verdeckt rund 5 % der Fläche und stört das Scannen nicht. Rückgabe als Daten-URL für ein img-Element.
 */
export async function totpQrCode(uri: string): Promise<string> {
  const qr = QRCode.create(mitLogo(uri), { errorCorrectionLevel: 'Q' });
  const n = qr.modules.size;
  const rand = 2;
  const gesamt = n + rand * 2;

  // Freifläche in der Mitte: etwa 22 % der Kantenlänge, ungerade Modulzahl
  let frei = Math.round(n * 0.22);
  if (frei % 2 === 0) frei += 1;
  const freiStart = Math.floor((n - frei) / 2);
  const freiEnde = freiStart + frei;

  const imSuchmuster = (r: number, c: number) =>
    (r < 7 && c < 7) || (r < 7 && c >= n - 7) || (r >= n - 7 && c < 7);
  const inMitte = (r: number, c: number) => r >= freiStart && r < freiEnde && c >= freiStart && c < freiEnde;

  const punkte: string[] = [];
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (!qr.modules.get(r, c) || imSuchmuster(r, c) || inMitte(r, c)) continue;
      punkte.push(`M${c + rand + 0.5} ${r + rand + 0.07}a0.43 0.43 0 1 0 0.001 0z`);
    }
  }

  const logoRand = 0.6;
  const logoGroesse = frei - logoRand * 2;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${gesamt} ${gesamt}" shape-rendering="geometricPrecision">`
    + `<rect width="${gesamt}" height="${gesamt}" fill="#ffffff"/>`
    + `<path d="${punkte.join('')}" fill="${FARBE}"/>`
    + suchmuster(rand, rand) + suchmuster(rand + n - 7, rand) + suchmuster(rand, rand + n - 7)
    + zeichen(rand + freiStart + logoRand, rand + freiStart + logoRand, logoGroesse)
    + `</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
